import { useEffect } from 'react'
import { useUserRestrictionsStore } from '../stores/userRestrictionsStore'

/**
 * React hook for accessing user restrictions
 *
 * Provides easy-to-use helper methods to check if specific features are allowed
 * for the current user. Automatically fetches restrictions on mount if cache is
 * expired, and refetches when the page becomes visible again.
 *
 * @example
 * ```tsx
 * function TransferPage() {
 *   const { canTransfer, isLoading } = useUserRestrictions()
 *
 *   if (isLoading) return <Spinner />
 *   if (!canTransfer()) return <RestrictedMessage />
 *
 *   return <TransferForm />
 * }
 * ```
 */
export function useUserRestrictions() {
  const {
    data,
    isLoading,
    error,
    fetchRestrictions,
    clearRestrictions,
    isRestrictionCacheValid,
  } = useUserRestrictionsStore()

  // Fetch restrictions on mount if cache is invalid
  useEffect(() => {
    if (!isRestrictionCacheValid()) {
      fetchRestrictions()
    }
  }, [fetchRestrictions, isRestrictionCacheValid])

  // Always refresh when page becomes visible (user returns to app)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchRestrictions(true) // Force fetch when coming back to app
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [fetchRestrictions])

  const restrictions = data?.restrictions ?? null

  return {
    // Raw data
    restrictions,
    data,
    isLoading,
    error,

    // Helper methods (returns true if allowed, false if restricted)
    // Returns false until data is loaded (fail-closed behavior)
    canTransfer: () => !!data && !restrictions?.p2p_transfer_disabled,
    canPay: () => !!data && !restrictions?.payment_disabled,
    canRedeemToBank: () => !!data && !restrictions?.banking_redemption_disabled,
    canRedeemToEwallet: () => !!data && !restrictions?.ewallet_redemption_disabled,
    canExportPrivateKey: () => !!data && !restrictions?.private_key_export_disabled,
    canManageBankAccounts: () => !!data && !restrictions?.banking_crud_disabled,
    canManageEwalletAccounts: () => !!data && !restrictions?.ewallet_crud_disabled,
    canUsePpob: () => !!data && !restrictions?.ppob_disabled,

    // Actions
    refresh: fetchRestrictions,
    clear: clearRestrictions,
  }
}
